import i18n from 'i18next';
import { createSlice } from '@reduxjs/toolkit';
import { toast } from '@oliasoft-open-source/react-ui-library';
import { apiCallBegan } from 'api/api.ts';
import translations from 'src/internationalisation/translation-map.json';
import { IAccessControlState, IAccessRightsBody } from 'client/common/interfaces/access-control.interfaces.ts';
import { AccessSubjectTypes, AccessTypes, HierarchyListTypesValues } from '@oliasoft/hierarchy-gui';
import type { IAccessRightsMetadata, IEntityAccessRights } from '@oliasoft/hierarchy-gui';

const initialState: IAccessControlState = {
  isFetching: false,
  isAdding: false,
  accessRights: [] as IEntityAccessRights[],
  metadata: {} as IAccessRightsMetadata,
  pricebookAccessRights: [],
};

const accessControl = createSlice({
  name: 'accessControl',
  initialState,
  reducers: {
    entityAccessRightsRequested: (accessControl) => {
      accessControl.isFetching = true;
    },
    entityAccessRightsRecieved: (accessControl, action) => {
      accessControl.isFetching = false;
      accessControl.accessRights = action.payload.accessRights || [];
      accessControl.metadata = action.payload.metadata || [];
    },
    entityAccessRightsFailed: (accessControl) => {
      accessControl.isFetching = false;
    },
    entityAccessRightsBatchSaveRequested: (accessControl) => {
      accessControl.isAdding = true;
    },
    entityAccessRightsBatchSaved: (accessControl) => {
      accessControl.isAdding = false;
    },
    entityAccessRightsBatchSaveFailed: (accessControl) => {
      accessControl.isAdding = false;
    },
    priceBookAccessRightsRecieved: (accessControl, action) => {
      accessControl.isFetching = false;
      accessControl.pricebookAccessRights = action.payload.pricebookAccessRights || [];
    },
  },
});

export const {
  entityAccessRightsBatchSaveRequested,
  entityAccessRightsBatchSaved,
  entityAccessRightsBatchSaveFailed,
  entityAccessRightsRequested,
  entityAccessRightsRecieved,
  entityAccessRightsFailed,
  priceBookAccessRightsRecieved,
} = accessControl.actions;
export default accessControl.reducer;

export const getEntityAccessRightsList = (id: string, type: string, subjecttype: AccessSubjectTypes) =>
  apiCallBegan({
    url: `/api/entity-access-rights-list?type=${type}&id=${id}&subjecttype=${subjecttype}`,
    method: 'GET',
    onStart: entityAccessRightsRequested.type,
    onSuccess: entityAccessRightsRecieved.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.accessControl_failedToGetAccessRights) as string,
        },
      });
      return {
        type: entityAccessRightsFailed.type,
      };
    },
  });

export const setAccessRightsBatch = (
  data: IAccessRightsBody[],
  type: HierarchyListTypesValues | 'pricebook',
  accesstype: AccessTypes,
  subjecttype: AccessSubjectTypes,
) =>
  apiCallBegan({
    url: `/api/manage-access-rights/batch?type=${type}&accesstype=${accesstype}&subjecttype=${subjecttype}`,
    method: 'POST',
    data,
    onStart: entityAccessRightsBatchSaveRequested.type,
    onSuccess: (response) => {
      toast({
        message: {
          type: 'Success',
          content: i18n.t(translations.accessControl_successfullyUpdatedAccessRights) as string,
        },
      });
      return {
        type: entityAccessRightsBatchSaved.type,
        payload: response,
      };
    },
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.accessControl_failedToSaveAccessRights) as string,
        },
      });
      return {
        type: entityAccessRightsBatchSaveFailed.type,
      };
    },
  });

export const getPricebookAccessRightsList = (subjecttype: AccessSubjectTypes) =>
  apiCallBegan({
    url: `/api/pricebook-access-rights-list?subjecttype=${subjecttype}`,
    method: 'GET',
    onStart: entityAccessRightsRequested.type,
    onSuccess: priceBookAccessRightsRecieved.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.accessControl_failedToGetAccessRights) as string,
        },
      });
      return {
        type: entityAccessRightsFailed.type,
      };
    },
  });
